/**
 * SourcesPanel — the right-hand "Sources" link-list shown for an AI answer
 * (shared by the AI Overview and each AI Mode turn). Shows up to 5 sources,
 * with a "Show all (N)" toggle when there are more.
 */

import React, { useState } from 'react';
import { AICitation } from '../ai-preview/types';
import styles from './index.module.scss';

interface SourcesPanelProps {
  sources: AICitation[];
  testId?: string;
}

const COLLAPSED_COUNT = 5;

const SourcesPanel: React.FC<SourcesPanelProps> = ({
  sources,
  testId = 'ai-preview-sources',
}) => {
  const [showAll, setShowAll] = useState(false);

  if (!sources.length) return null;

  const hasMore = sources.length > COLLAPSED_COUNT;
  const visible = showAll ? sources : sources.slice(0, COLLAPSED_COUNT);

  return (
    <nav
      className={styles.sourcesPanel}
      aria-label="AI answer sources"
      data-testid={testId}
    >
      <span className={styles.sourcesLabel}>Sources</span>
      <ul className={styles.sourcesList}>
        {visible.map((source) => (
          <li key={source.url} className={styles.sourceItem}>
            <a href={source.url} className={styles.sourceLink}>
              {source.title}
            </a>
            {source.snippet && (
              <p className={styles.sourceSnippet}>{source.snippet}</p>
            )}
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          className={styles.showAll}
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
          data-testid="sources-show-all"
        >
          {showAll ? 'Show less' : `Show all (${sources.length})`}
        </button>
      )}
    </nav>
  );
};

export default SourcesPanel;
